import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { ChevronDown, Check } from "lucide-react";

export default function DropdownSelect({
  options = [],
  value,
  onChange,
  placeholder = "Selectează",
  disabled = false,
  ariaLabel,
}) {
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value) || null,
    [options, value]
  );

  // click în afară → închide dropdown
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [open]);

  // gestionare tastatură global
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape" || event.key === "Tab") {
        event.preventDefault();
        setOpen(false);
        return;
      }

      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev < options.length - 1 ? prev + 1 : 0
        );
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : options.length - 1
        );
      } else if (event.key === "Enter") {
        event.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < options.length) {
          const selected = options[highlightedIndex];
          onChange(selected.value);
          setOpen(false);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, highlightedIndex, options, onChange]);

  // când se deschide dropdownul → setează highlight pe opțiunea curentă
  useEffect(() => {
    if (open) {
      const currentIndex = options.findIndex((opt) => opt.value === value);
      setHighlightedIndex(currentIndex >= 0 ? currentIndex : 0);
    }
  }, [open, options, value]);

  // 👉 toggling clar controlat doar de acest handler
  const handleTriggerClick = (event) => {
    event.preventDefault();
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  const handleTriggerKeyDown = (event) => {
    if (disabled) return;
    if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
    }
  };

  const handleSelectOption = (optionValue) => {
    onChange(optionValue);
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
        className={clsx(
          "w-full rounded-lg border px-3 py-2 text-left text-sm flex items-center justify-between bg-white transition focus:outline-none focus:ring-2 focus:ring-blue-300",
          disabled
            ? "cursor-not-allowed bg-gray-100 text-gray-400 border-gray-200"
            : "border-gray-200 text-gray-700 hover:border-blue-300"
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span>{selectedOption ? selectedOption.label : placeholder}</span>
        <ChevronDown
          className={clsx(
            "h-4 w-4 text-gray-500 transition-transform",
            open && "rotate-180"
          )}
        />
      </button>

      {open &&
        <div className="absolute z-[10006] left-0 right-0 mt-1">
          <div className="rounded-xl border border-gray-200 bg-white shadow-lg max-h-60 overflow-y-auto">
            <ul role="listbox" aria-label={ariaLabel}>
              {options.map((option, index) => {
                const isSelected = option.value === value;
                const isHighlighted = index === highlightedIndex;

                return (
                  <li key={option.value}>
                    <button
                      type="button"
                      className={clsx(
                        "w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left transition-colors",
                        isHighlighted
                          ? "bg-blue-100 text-blue-800"
                          : isSelected
                          ? "bg-blue-50 text-blue-700"
                          : "text-gray-700 hover:bg-gray-100"
                      )}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={() => handleSelectOption(option.value)}
                      role="option"
                      aria-selected={isSelected}
                    >
                      <span>{option.label}</span>
                      {isSelected && <Check className="h-4 w-4" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>}
    </div>
  );
}
